/**
 * `@tesoratag` — finding somebody by the name they are paid to (go-wallet#1066).
 *
 * # Two routes, and they answer different questions
 *
 *   GET  /v1/handle                              what MY tag is
 *   POST /v1/internal-transfers/recipients       who @somebody is
 *
 * The second is the lookup: it confirms who one address belongs to, stores
 * nothing, and returns a MASKED name and a currency and nothing else. It is not
 * a directory and it is not a search: there is no endpoint that lists tags, by
 * design, and this app must not build one out of repeated lookups.
 *
 * # A miss is not a mistyping
 *
 * `404 no_recipient` covers "nobody holds that name", "that account is closed"
 * and "that account cannot be credited" as ONE answer — a distinct message for
 * the second would report a stranger's compliance status to anybody who typed
 * their public name. So this reports one thing: we cannot pay that tag.
 *
 * A `400 invalid_handle` IS a mistyping and says so, because the shape is
 * something the person can fix.
 *
 * # `503` is not "nobody"
 *
 * Telling somebody their collaborator is not a customer when the truth is that
 * we could not look is a false statement about a third party. The two states are
 * kept apart here for the same reason `accounts.ts` keeps `none` and
 * `unavailable` apart.
 */
import { ApiError, customerMessage } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export interface TagMatch {
  /** The masked name, exactly as the lookup returns it. Never the real one. */
  readonly displayName: string
  /** The currency their account is held in. */
  readonly currency: string
  /**
   * Present only when the tag typed is in quarantine — the name its owner
   * answers to NOW.
   *
   * Shown rather than swallowed: paying under a name somebody has left, with
   * nothing on screen saying so, is the worse of the two outcomes.
   */
  readonly currentTag?: string
}

export type TagLookupState =
  | { readonly status: 'idle' }
  | { readonly status: 'looking' }
  | { readonly status: 'found'; readonly tag: string; readonly match: TagMatch }
  /** Nobody we can pay answers to that tag. ONE answer for three causes. */
  | { readonly status: 'no_match' }
  /** The shape is wrong, which is something the person can fix. */
  | { readonly status: 'malformed'; readonly reason: string }
  /** We could not look. NEVER rendered as "no such person". */
  | { readonly status: 'unavailable'; readonly reason: string }

/** Strip a leading `@` and trim. The backend normalises; this is for display. */
export function bareTag(raw: string): string {
  return raw.trim().replace(/^@+/, '')
}

interface LookupDTO {
  display_name?: unknown
  currency?: unknown
  current_handle?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

export async function lookupTag(api: WalletApi, raw: string): Promise<TagLookupState> {
  const tag = bareTag(raw)
  if (tag === '') return { status: 'idle' }

  let dto: LookupDTO
  try {
    dto = await api.post<LookupDTO>(
      '/v1/internal-transfers/recipients',
      { address_kind: 'handle', address: tag },
      {
        // A LOOKUP THAT STORES NOTHING. Its contract publishes no
        // Idempotency-Key because there is no effect to replay: it confirms who
        // an address belongs to and writes no row. The exemption is named rather
        // than taken silently, which is what `replayGuardedBy` is for.
        replayGuardedBy: 'nothing is written: the recipient lookup is a read that returns a mask',
      },
    )
  } catch (err) {
    if (err instanceof ApiError) {
      // 404 is an ANSWER — one answer for three causes, see the header.
      if (err.status === 404) return { status: 'no_match' }
      if (err.status === 400) return { status: 'malformed', reason: err.message }
    }
    return {
      status: 'unavailable',
      reason: customerMessage(err, 'Please try again in a moment.'),
    }
  }

  const displayName = str(dto?.display_name)
  const currency = str(dto?.currency)
  if (displayName === undefined || currency === undefined) {
    // A confirmation with no name is not a confirmation. Rendering a blank one
    // would let somebody accept a match they were never shown.
    return { status: 'unavailable', reason: 'that answer arrived without a name to confirm' }
  }

  const currentTag = str(dto?.current_handle)
  return {
    status: 'found',
    tag,
    match: { displayName, currency, ...(currentTag ? { currentTag } : {}) },
  }
}

/** Keep somebody, addressed by their tag. Returns undefined on success. */
export async function saveByTag(
  api: WalletApi,
  raw: string,
  label: string,
): Promise<string | undefined> {
  try {
    await api.post(
      '/v1/saved-recipients',
      { address_kind: 'handle', address: bareTag(raw), label },
      {
        // The database decides, not a header: `saved_recipients` is UNIQUE on
        // (customer_id, recipient_account_id) with ON CONFLICT DO UPDATE, so a
        // replay writes the same row rather than a second one. No money moves.
        replayGuardedBy:
          'UNIQUE (customer_id, recipient_account_id) with ON CONFLICT DO UPDATE: a replay is the same row',
      },
    )
  } catch (err) {
    return customerMessage(err, 'We could not keep them just now. Please try again.')
  }
  return undefined
}

export type MyTagState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tag: string }
  /**
   * Claimed and WAITING ON A REVIEW. It is held — nobody else can take it — and
   * it does not resolve yet.
   *
   * Kept apart from `ready` because a screen that showed it as live would put a
   * working-looking payment address in front of somebody whose payers would
   * reach nobody.
   */
  | { readonly status: 'pending'; readonly tag: string }
  /** They have not claimed one. NOT a failure to look. */
  | { readonly status: 'none' }
  | { readonly status: 'unavailable' }

interface MyTagDTO {
  handle?: unknown
  state?: unknown
}

/**
 * What my own tag is, so it can be shown and handed out.
 *
 * AN EMPTY HANDLE IS A 200, not a 404. The contract is explicit: "empty is a
 * real answer and `state` is what says so", and "a client that read the empty
 * string as a name would put `@` on a pay-me link that goes nowhere". The 404
 * branch stays anyway — a deployment that does not mount the route answers one,
 * and that is `unavailable` rather than `none`.
 */
export async function fetchMyTag(api: WalletApi): Promise<MyTagState> {
  let dto: MyTagDTO
  try {
    dto = await api.get<MyTagDTO>('/v1/handle')
  } catch {
    // We could not look. NEVER `none`: telling somebody to claim a name they may
    // already hold is the wrong instruction, and it is the one that follows from
    // collapsing these two.
    return { status: 'unavailable' }
  }
  const tag = str(dto?.handle)
  if (tag === undefined) return { status: 'none' }
  return str(dto?.state) === 'pending_review'
    ? { status: 'pending', tag }
    : { status: 'ready', tag }
}

export type AvailabilityState =
  | { readonly status: 'idle' }
  | { readonly status: 'checking' }
  /** Free, and yours the moment you claim it. */
  | { readonly status: 'free'; readonly tag: string }
  /**
   * Claimable, but it would be HELD pending an impersonation decision.
   *
   * Surfaced BEFORE the claim, which is the contract's own reason for
   * publishing it: "so the customer chooses knowingly rather than discovering
   * that their handle does not work".
   */
  | { readonly status: 'needs_review'; readonly tag: string }
  /** Not claimable. `reason` is a stable CODE, never a sentence. */
  | { readonly status: 'taken'; readonly reason: string }
  /** We could not check. NEVER rendered as free — that would invite a claim. */
  | { readonly status: 'unavailable' }

interface AvailabilityDTO {
  handle?: unknown
  available?: unknown
  needs_review?: unknown
  reason?: unknown
}

/**
 * Is this tag free?
 *
 * The reason is a machine CODE and the sentence is ours, which is the contract's
 * split: "it is a code and never a sentence: the sentence is the copy catalog's
 * and changes without the client changing." So this maps the codes it knows and
 * falls back to saying only that the name is unavailable — which is true for
 * every code, including ones this bundle has never seen.
 */
export async function checkTag(api: WalletApi, raw: string): Promise<AvailabilityState> {
  const tag = bareTag(raw)
  if (tag === '') return { status: 'idle' }

  let dto: AvailabilityDTO
  try {
    dto = await api.get<AvailabilityDTO>(
      `/v1/handles/availability?handle=${encodeURIComponent(tag)}`,
    )
  } catch {
    return { status: 'unavailable' }
  }

  const canonical = str(dto?.handle) ?? tag
  if (dto?.available !== true) {
    return { status: 'taken', reason: str(dto?.reason) ?? '' }
  }
  return dto?.needs_review === true
    ? { status: 'needs_review', tag: canonical }
    : { status: 'free', tag: canonical }
}

/**
 * What to say about a name that cannot be taken.
 *
 * Every branch is a true sentence about the same fact — the name is not
 * available — and the specific ones only add WHY where that is something the
 * person can act on. An unknown code says the general thing rather than
 * guessing, because a bundle in somebody's browser outlives the codes it knew.
 */
export function unavailableBecause(reason: string): string {
  switch (reason) {
    case 'taken':
      return 'Somebody already has that one.'
    case 'reserved':
      return 'That one is reserved.'
    case 'already_yours':
      return 'That one is already yours.'
    case 'cooldown':
      return 'You changed your tag recently, so this one has to wait a little.'
    case 'charset':
      return 'Letters, digits and single hyphens only — no dots and no underscores.'
    case 'too_short':
      return 'A bit longer: three characters at least.'
    case 'too_long':
      return 'A bit shorter: thirty characters at most.'
    default:
      return 'That one is not available.'
  }
}

export type ClaimResult =
  | { readonly ok: true; readonly tag: string; readonly pending: boolean }
  | { readonly ok: false; readonly reason: string }

interface ClaimDTO {
  handle?: unknown
  state?: unknown
}

/**
 * Claim a tag.
 *
 * `409` is its own answer and is NOT an error to apologise for: the name is
 * taken, reserved, already yours, or a cooldown has not run. Every one of those
 * is something the person can act on, and the backend writes the sentence — this
 * renders it rather than inventing a friendlier one that would be less true.
 */
export async function claimTag(api: WalletApi, raw: string): Promise<ClaimResult> {
  let dto: ClaimDTO
  try {
    dto = await api.post<ClaimDTO>(
      '/v1/handle',
      { handle: bareTag(raw) },
      {
        // A name is claimed once. Migration 131's partial indexes allow one live
        // handle per customer and one owner per name, so a replay of the same
        // claim finds it already theirs and is refused — it cannot become a
        // second claim. No money moves on this route.
        replayGuardedBy:
          'the registry is unique on the name and on one live handle per customer: a replay is refused, not repeated',
      },
    )
  } catch (err) {
    return { ok: false, reason: customerMessage(err, 'We could not claim that just now. Please try again.') }
  }
  const tag = str(dto?.handle)
  if (tag === undefined) {
    return { ok: false, reason: 'that claim came back without naming the tag it took' }
  }
  return { ok: true, tag, pending: str(dto?.state) === 'pending_review' }
}
